<template>
  <div class="policy-type tw-flex tw-flex-col tw-items-center tw-mt-8">
    <div class="tw-w-full">
      <h2 style="color: #114cac" class="tw-text-2xl tw-font-semibold tw-mb-4">
        Policy Type
      </h2>

      <!-- Policy Start Date -->
      <div class="tw-mb-6 tw-relative tw-cursor-pointer">
        <v-form ref="form">
          <v-text-field
              v-model="startPolicyDate"
              label="Policy Start From"
              type="date"
              class="tw-w-full policy-start-date"
              :rules="[required('Policy Start From')]"
              placeholder="Enter Policy Start Date"
              :min="minDate"
              outlined
          ></v-text-field>
        </v-form>
      </div>

      <div
          v-if="exceedsMaxSumInsured"
          class="pa-2 tw-rounded tw-mb-4 primary--text tw-w-full tw-flex tw-bg-gray-300"
      >
        <v-icon class="primary--text c-blue-text tw-font-bold tw-mr-3">mdi-alert</v-icon>
        <p class="mb-0">
          Kindly contact Customer Care on 0709 912 777 or visit any of our branches countrywide to purchase Comprehensive or Bima Bamba cover.
        </p>
      </div>

      <!-- Custom Policy Selection Options -->
      <div
          v-for="(policy, index) in filteredPolicies"
          :key="index"
      >
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <div
                @click="selectPolicy(policy)"
                :class="policyClass(policy)"
                v-on="on"
                v-bind="attrs"
                class="tw-cursor-pointer"
            >
              <div class="tw-flex tw-justify-between tw-items-center">
                <!-- Policy Icon and Name -->
                <div class="tw-flex tw-items-center">
                  <v-icon :color="policyIconColor(policy)" class="tw-mr-2">
                    {{ policy.icon }}
                  </v-icon>
                  <div :class="policyNameClass(policy)">
                    {{ policy.name }}
                  </div>
                </div>
                <!-- Policy Description and Price -->
                <div class="tw-flex tw-flex-col tw-items-end">
                  <div class="tw-text-sm tw-text-blue-600">
                    {{ policy.description }}
                  </div>
                  <div
                      v-for="(plan, i) in policy.paymentPlans"
                      :key="i"
                      class="tw-text-black-600"
                  >
                    {{ plan.distribution }}
                  </div>
                </div>
              </div>
            </div>
          </template>
          <span>Select your preffered policy type</span>
        </v-tooltip>
      </div>

      <div
          v-if="disableTpoNewBusiness && filteredPolicies.length === 0"
          class="tw-w-full tw-mb-4 tw-p-4 tw-rounded-lg tw-bg-orange-100 tw-text-blue-600">
        {{ tpoDisabledMessage }}
      </div>

      <!-- Chargeable Benefits Section -->
      <div
          v-if="isBimaBambaSelected || isComprehensiveSelected"
          class="chargeable-benefits tw-flex tw-flex-col tw-gap-4">
        <h3 class="tw-text-blue-600 tw-font-semibold">Chargeable Benefits</h3>
        <p class="tw-text-blue-600">Select some of our chargeable benefits</p>

        <!-- Bima Bamba Benefits (Only show these when Bima Bamba is selected) -->
        <template v-if="isBimaBambaSelected">
          <!-- Courtesy Car Section -->
          <div
              class="benefit-option tw-flex tw-items-center tw-justify-between tw-gap-4">
            <div class="tw-flex tw-items-center">
              <v-icon color="blue" class="tw-mr-2">mdi-car</v-icon>
              <span class="tw-font-medium tw-text-blue-600">Courtesy Car</span>
            </div>
            <v-radio-group
                v-model="courtesyCarOption"
                row
                class="tw-flex-grow tw-justify-end lg:tw-pl-16">
              <v-radio
                  label="10 Days"
                  value="10"
                  class="radio-button"></v-radio>
              <v-radio
                  label="20 Days"
                  value="20"
                  class="radio-button"></v-radio>
            </v-radio-group>
          </div>

          <!-- AA Membership Section -->
          <div
              class="benefit-option tw-flex tw-items-center tw-justify-between tw-gap-4">
            <div class="tw-flex tw-items-center">
              <v-icon color="blue" class="tw-mr-1">mdi-alpha-a-a</v-icon>
              <span class="tw-font-medium tw-text-blue-600">AA Membership</span>
            </div>
            <v-radio-group
                v-model="aaMembership"
                row
                class="tw-flex-grow tw-justify-end lg:tw-pr-16">
              <v-radio label="Yes" value="yes" class="radio-button"></v-radio>
              <v-radio label="No" value="no" class="radio-button"></v-radio>
            </v-radio-group>
          </div>
        </template>

        <!-- Comprehensive Benefits (Only show these when Comprehensive is selected) -->
        <template v-if="isComprehensiveSelected">
          <!-- Courtesy Car Section -->
          <div
              class="benefit-option tw-flex tw-items-center tw-justify-between tw-gap-4">
            <div class="tw-flex tw-items-center">
              <v-icon color="blue" class="tw-mr-2">mdi-car</v-icon>
              <span class="tw-font-medium tw-text-blue-600">Courtesy Car</span>
            </div>
            <v-radio-group
                v-model="courtesyCarOption"
                row
                class="tw-flex-grow tw-justify-end lg:tw-pl-16">
              <v-radio
                  label="10 Days"
                  value="10"
                  class="radio-button"></v-radio>
              <v-radio
                  label="20 Days"
                  value="20"
                  class="radio-button"></v-radio>
            </v-radio-group>
          </div>

          <!-- AA Membership Section -->
          <div
              class="benefit-option tw-flex tw-items-center tw-justify-between tw-gap-4">
            <div class="tw-flex tw-items-center">
              <v-icon color="blue" class="tw-mr-1">mdi-alpha-a-a</v-icon>
              <span class="tw-font-medium tw-text-blue-600">AA Membership</span>
            </div>
            <v-radio-group
                v-model="aaMembership"
                row
                class="tw-flex-grow tw-justify-end lg:tw-pr-16">
              <v-radio label="Yes" value="yes" class="radio-button"></v-radio>
              <v-radio label="No" value="no" class="radio-button"></v-radio>
            </v-radio-group>
          </div>

          <!-- Political Violence & Terrorism Section -->
          <div
              class="benefit-option tw-flex tw-items-center tw-justify-between tw-gap-4">
            <div class="tw-flex tw-items-center">
              <v-icon color="blue" class="tw-mr-1">mdi-shield-alert</v-icon>
              <span class="tw-font-medium tw-text-blue-600"
              >Political Violence & Terrorism</span
              >
            </div>
            <v-radio-group
                v-model="politicalViolence"
                row
                class="tw-flex-grow tw-justify-end lg:tw-pr-16"
                @click="benefits()">
              <v-radio label="Yes" value="yes" class="radio-button"></v-radio>
              <v-radio
                  label="No"
                  value="no"
                  class="radio-button"
                  disabled></v-radio>
            </v-radio-group>
          </div>

          <!-- Excess Protector Section -->
          <div
              class="benefit-option tw-flex tw-items-center tw-justify-between tw-gap-4">
            <div class="tw-flex tw-items-center">
              <v-icon color="blue" class="tw-mr-1">mdi-shield-check</v-icon>
              <span class="tw-font-medium tw-text-blue-600"
              >Excess Protector</span
              >
            </div>
            <v-radio-group
                v-model="excessProtector"
                row
                class="tw-flex-grow tw-justify-end lg:tw-pr-16">
              <v-radio label="Yes" value="yes" class="radio-button"></v-radio>
              <v-radio
                  label="No"
                  value="no"
                  class="radio-button"
                  disabled></v-radio>
            </v-radio-group>
          </div>
        </template>
      </div>

      <!--checkbox to add images-->
      <div v-if="isNotThirdPartyPolicy"
           class="tw-bg-blue-100 tw-mt-4 tw-p-4 tw-mb-4 tw-border tw-rounded-lg tw-shadow vehicle-upload-section">
        <div class="tw-flex tw-items-center tw-justify-between tw-flex-col md:tw-flex-row tw-gap-4 md:tw-gap-0">
          <div class="tw-flex tw-items-center tw-gap-2">
            <div class="tw-bg-white tw-text-white tw-p-2 tw-rounded-lg">
              <img
                  :src="'/icons/car.png'"
                  alt="Car Icon"
                  class="tw-w-6 tw-h-6"
              />
            </div>
            <span class="tw-text-lg tw-font-semibold tw-text-blue-600">Vehicle photo Uploads</span>
          </div>
          <div class="tw-flex tw-items-center tw-gap-2">
            <input
                type="checkbox"
                id="vehicle-upload"
                v-model="isChecked"
                @change="scrollToUploadSection"
                class="tw-w-4 tw-h-4 tw-text-blue-600 tw-border-gray-300 tw-rounded focus:tw-ring-blue-500"
            />
            <label for="vehicle-upload" class="tw-text-sm tw-text-gray-600">
              Check to upload Vehicle Photos
            </label>
          </div>
        </div>
      <!-- Photo Upload Grid -->
      <div v-if="isChecked" class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-4 tw-mt-4 photo-upload-grid">
        <div
            v-for="(upload, index) in uploadTypes"
            :key="index"
            class="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-4 tw-flex tw-flex-col tw-items-center tw-justify-center tw-space-y-3 tw-relative tw-min-h-[280px]"
            :class="{'tw-border-blue-500 tw-border-2': imagePreviews[upload.title]}"
        >
          <!-- Empty State -->
          <div
              v-if="!imagePreviews[upload.title]"
              class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-full tw-w-full tw-space-y-4"
          >
            <div class="tw-bg-blue-50 tw-p-4 tw-rounded-full">
              <img
                  :src="'/icons/car.png'"
                  alt="Car Icon"
                  class="tw-w-6 tw-h-6"
              />
            </div>
            <div class="tw-text-center">
              <h3 class="tw-font-semibold tw-text-gray-900">{{ upload.title }}</h3>
              <p class="tw-text-sm tw-text-gray-500 tw-mt-1">{{ upload.subtitle }}</p>
            </div>
            <button
                class="tw-bg-blue-600 tw-text-white tw-px-6 tw-py-2 tw-rounded-lg tw-text-sm hover:tw-bg-blue-700 tw-transition-colors tw-flex tw-items-center tw-gap-2"
                @click="handleUploadClick(upload.title, index)"
            >
              <img :src="'/icons/upload.png'" alt="Upload Icon" class="tw-w-5 tw-h-5 tw-mr-2" />              Upload Photo
            </button>
          </div>

          <!-- Preview State -->
          <div
              v-if="imagePreviews[upload.title]"
              class="tw-relative tw-w-full tw-h-64 tw-rounded-lg tw-overflow-hidden"> <!-- Changed from h-48 to h-64 -->
            <!-- Image -->
            <img
                :src="imagePreviews[upload.title]"
                :alt="upload.title"
                class="tw-w-full tw-h-full tw-object-cover"
            />

            <!-- Floating Icons Container -->
            <div class="tw-absolute tw-inset-0 tw-p-2">
              <div class="tw-flex tw-justify-end tw-gap-2">
                <!-- View Icon -->
                <button
                    @click="viewImage(upload.title)"
                    aria-label="View image"
                    class="tw-w-8 tw-h-8 tw-flex tw-items-center tw-justify-center tw-bg-white tw-rounded-full tw-shadow hover:tw-bg-gray-50 tw-transition-colors"
                >
                  <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" class="tw-w-5 tw-h-5 tw-text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </button>

                <!-- Delete Icon -->
                <button
                    @click="deleteImage(upload.title)"
                    aria-label="Delete image"
                    class="tw-w-8 tw-h-8 tw-flex tw-items-center tw-justify-center tw-bg-white tw-rounded-full tw-shadow hover:tw-bg-gray-50 tw-transition-colors"
                >
                  <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" class="tw-w-5 tw-h-5 tw-text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>

            <!-- Photo Label and Change Photo Link -->
            <div class="tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-bg-white tw-bg-opacity-90 tw-p-2">
              <p class="tw-text-sm tw-font-medium tw-text-gray-900">{{ upload.title }}</p>
              <button
                  @click="handleUploadClick(upload.title, index)"
                  class="tw-text-xs tw-text-blue-600 hover:tw-text-blue-800 tw-transition-colors"
              >
                Change Photo
              </button>
            </div>
          </div>

          <input
              type="file"
              class="tw-hidden"
              accept="image/*"
              :ref="`fileInput_${index}`"
              @change="handleFileChange($event, upload.title)"
          />
        </div>
      </div>

      <!-- Image Preview Modal -->
      <div
          v-if="selectedImage"
          class="tw-fixed tw-inset-0  tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-z-50"
          @click="selectedImage = null"
      >
        <div
            class="tw-bg-white tw-rounded-lg tw-relative !tw-w-[80%] tw-overflow-hidden relative"
            style="max-width: 80vw;  height: 100vh;"
            @click.stop
        >
          <!-- Header -->
          <div class="tw-absolute tw-top-0 tw-left-0 tw-right-0 tw-p-4 tw-bg-white tw-border-b tw-z-10 tw-flex tw-justify-end">
            <button
                @click="selectedImage = null"
                class="tw-absolute tw-top-2 tw-right-2 tw-bg-white tw-p-2 tw-rounded-full tw-shadow-md hover:tw-bg-gray-100"
            >
              <span class="tw-material-icons">close</span>
            </button>
          </div>

          <!-- Image Container -->
          <div class="tw-w-full tw-h-full tw-pt-16 tw-pb-4 tw-px-4">
            <img
                :src="selectedImage"
                alt="Preview"
                class="tw-w-full tw-h-full tw-object-contain"
                style="object-fit: contain;"
            />
          </div>
        </div>
      </div>
      </div>

      <!-- Proceed Button -->
      <div class="tw-flex tw-justify-center tw-mt-6">
        <v-btn
            @click="handleProceed"
            color="primary"
            :disabled="!selectedPolicy || !startPolicyDate"
            class="tw-w-full md:tw-w-auto tw-px-8 tw-bg-blue-600 tw-text-white tw-font-semibold">
          Proceed & select payment plan
        </v-btn>
      </div>
    </div>
    <DoubleInsurance
        v-if="showDoubleInsurance"
        @close="showDoubleInsurance = false" />
    <SelectPaymentPlan
        v-if="showSelectPaymentPlan"
        @selected="handlePaymentPlan"
        @close="showSelectPaymentPlan = false" />
  </div>
</template>

<script>
/* eslint-disable */
import axios from "axios";
import validations from "@/utils/validations";
import DoubleInsurance from "./DoubleInsurance.vue";
import SelectPaymentPlan from "@/components/tpo/SelectPaymentPlan.vue";
import { changeFormat } from "@/utils/time";
import unleashMixin from "@/mixins/unleashMixin";

const TPO_GATE_FEATURE_FLAG = "TPO_BUSINESS";
const THIRD_PARTY = "Third Party";
const TPO_DISABLED_MESSAGE =
  "Important Notice: We no longer support the purchase of Third Party Only (TPO) motor insurance policies through our digital channels. For assistance with your insurance requirement, please contact our Customer Service team at customer.service@apollo.co.ke";

export default {
  name: "PolicyType",
  mixins: [unleashMixin],
  components: { SelectPaymentPlan, DoubleInsurance },
  data() {
    return {
      showDoubleInsurance: false,
      selectedPolicy: null,
      startPolicyDate: "",
      policies: [],
      minDate: this.calculateMinDate(),
      benefits: [],
      courtesyCarOption: null,
      aaMembership: null,
      politicalViolence: "yes",
      excessProtector: "yes",
      bimaBambaPremium: 0,
      details: {},
      filteredPolicies: [],
      showSelectPaymentPlan: false,
      paymentPlan: null,
      isChecked: false,
      selectedImage: null,
      uploadProgress: {},
      uploadTypes: [
        {
          title: 'Vehicle front photo',
          subtitle: 'Clearly capturing the vehicle registration',
        },
        {
          title: 'Vehicle back photo',
          subtitle: 'Clearly capturing the vehicle registration number',
        },
        {
          title: 'Vehicle right side photo',
          subtitle: 'Clearly capturing the vehicle side',
        },
        {
          title: 'Vehicle left side photo',
          subtitle: 'Clearly capturing the vehicle side',
        },
        {
          title: 'Vehicle windscreen',
          subtitle: 'clear picture',
        },
        {
          title: 'Vehicle Odometer',
          subtitle: 'Clear picture',
        },
      ],
      imagePreviews: {},
    };
  },
  computed: {
    disableTpoNewBusiness() {
      return !this.isFlagEnabled(TPO_GATE_FEATURE_FLAG);
    },
    tpoDisabledMessage() {
      return TPO_DISABLED_MESSAGE;
    },
    isNotThirdPartyPolicy() {
      return this.selectedPolicy?.name !== 'Third Party';
    },
    vehicleAge() {
      if (this.details.vehicle?.yearOfManufacture) {
        const currentYear = new Date().getFullYear();
        return currentYear - this.details.vehicle.yearOfManufacture;
      }
      return 0; // Default age if no data
    },
    exceedsMaxSumInsured() {
      return this.details.vehicle?.value > 10000000;
    },
    isThirdPartyOnly() {
      return (
        this.details.vehicle?.value < 600000 ||
        this.exceedsMaxSumInsured ||
        this.vehicleAge > 15 ||
        this.details.vehicle?.quoteExcluded !== "WHITELISTED"
      );
    },
    isBimaBambaSelected() {
      return this.selectedPolicy && this.selectedPolicy.name === "Bima Bamba";
    },
    isComprehensiveSelected() {
      return (
        this.selectedPolicy && this.selectedPolicy.name === "Comprehensive"
      );
    },
    policyIconColor() {
      return (policy) =>
        this.selectedPolicy === policy ? "green" : "grey lighten-1";
    },
    policyNameClass() {
      return (policy) => ({
        "tw-text-blue-600 tw-font-bold": this.selectedPolicy === policy,
        "tw-text-lg tw-font-medium tw-text-blue-600": true,
      });
    },
    policyClass() {
      return (policy) => [
        "tw-w-full tw-mb-4 tw-border tw-border-grey-300 tw-rounded-lg tw-p-4 tw-bg-white tw-shadow-sm hover:tw-shadow-md tw-cursor-pointer",
        { "tw-border-green-600 tw-border-4": this.selectedPolicy === policy },
      ];
    },
  },
  watch: {
    'selectedPolicy': {
      handler(newValue) {
        if (newValue) {
          this.$refs.form.validate();
        }
      },
    },
  },
  async mounted() {
    this.fetchDetails();
    try {
      const ratesResponse = await axios.get("/motor/api/premium/rates");
      const rates = ratesResponse.data.data;
      const bimapremium = "Payment plan:";
      const check = "mdi-shield-check";

      // Find the Bima Bamba data to store the minimum premium
      const bimaBambaData = rates.find(
        (rate) => rate.coverType === "Bima Bamba"
      );
      this.bimaBambaPremium = bimaBambaData ? bimaBambaData.minimumPremium : 0;

      this.policies = [
  {
    name: "Comprehensive",
    description: bimapremium,
    price: `Kshs. ${rates.find((item) => item.coverType === 'Comprehensive')?.minimumPremium?.toLocaleString() || ''}`,
    paymentPlans: [
      { distribution: "100%" },
      { distribution: "40% - 60%" },
      { distribution: "40% - 30% - 30%" }
    ],
    icon: check,
  },
  {
    name: "Bima Bamba",
    description: bimapremium,
    price: '12 Monthly Payments',
    paymentPlans: [
      { distribution: "12 Monthly Payments" }
    ],
    icon: check,
  },
  {
    name: "Third Party",
    description: bimapremium,
    price: `Kshs. ${rates.find((item) => item.coverType === 'Third Party')?.minimumPremium?.toLocaleString() || ''}`,
    paymentPlans: [
      { distribution: "100%" }
    ],
    icon: check,
  }
];

      this.filterPolicies();
      const benefitsResponse = await axios.get(
        "/motor/api/premium/fixedBenefitRates"
      );
      this.benefits = benefitsResponse.data.data;
      const savedPolicyTypes = JSON.parse(localStorage.getItem('savedPolicyTypes')) || {};
      if (savedPolicyTypes.selectedPolicy) {
        const matchedPolicy = this.filteredPolicies.find(
            policy => policy.name === savedPolicyTypes.selectedPolicy.name
        );

        if (matchedPolicy) {
          this.selectedPolicy = matchedPolicy;
          this.startPolicyDate = savedPolicyTypes.startPolicyDate || "";
        }
      }
    } catch (error) {
      this.$toast.error("Error during API calls:", error);
    }
  },
  methods: {
    scrollToUploadSection() {
      this.$nextTick(() => {
        if (this.isChecked) {
          const uploadSection = document.querySelector('.vehicle-upload-section');
          if (uploadSection) {
            uploadSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        } else {
          const headerSection = document.querySelector('.vehicle-upload-section');
          if (headerSection) {
            headerSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }
      });
    },
    handleUploadClick(photoType, index) {
      this.$refs[`fileInput_${index}`][0].click();
    },
    async uploadFile(file, photoType) {
      try {
        const storedData = JSON.parse(localStorage.getItem('formPayload'));
        const formData = new FormData();
        formData.append('file', file);
        formData.append('fileName', photoType);
        formData.append('customerId', storedData.customerId);
        formData.append('quoteId', storedData.quoteId);
        await axios.post('/motor/api/quote/document/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            this.uploadProgress[photoType] = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          },
        });
        this.$toast.success(`${photoType} uploaded successfully`);
      } catch (error) {
        this.$toast.error(`Error uploading ${photoType}`);
        // Handle error
      } finally {
        delete this.uploadProgress[photoType];
      }
    },
    viewImage(photoType) {
      this.selectedImage = this.imagePreviews[photoType];
    },
    deleteImage(photoType) {
      // eslint-disable-next-line no-restricted-globals,no-alert
      if (confirm('Are you sure you want to delete this image?')) {
        this.$delete(this.imagePreviews, photoType);
        // Reset the file input
        const index = this.uploadTypes.findIndex(type => type.title === photoType);
        if (index !== -1) {
          this.$refs[`fileInput_${index}`][0].value = ''; // Corrected ref syntax using template literals
        }
        this.$toast.success('Image deleted successfully (from preview)'); // Added feedback for delete action
        // call backend API to delete the image from the server below.
      }
    },

    handleFileChange(event, photoType) {
      const file = event.target.files[0];
      if (file) {
        if (file.size > 5 * 1024 * 1024) { // 5MB limit
          this.$toast.error('File size should not exceed 5MB');
          return;
        }

        if (!file.type.startsWith('image/')) {
          this.$toast.error('Please upload only image files');
          return;
        }

        this.processImage(file, photoType);
      }
    },

    processImage(file, photoType) {
      const reader = new FileReader();
      reader.onload = (e) => {
        this.$set(this.imagePreviews, photoType, e.target.result);
        this.uploadFile(file, photoType);
        this.$toast.success('Image uploaded successfully');
      };
      reader.onerror = () => {
        this.$toast.error('Error processing image');
      };
      reader.readAsDataURL(file);
    },
    handleProceed() {
      if (
        this.selectedPolicy?.name === "Third Party" ||
        this.selectedPolicy?.name === "Bima Bamba"
      ) {
        this.proceedToPayment();
      } else {
        this.showSelectPaymentPlan = true;
      }
      localStorage.setItem('savedPolicyTypes', JSON.stringify({
        selectedPolicy: this.selectedPolicy,
        startPolicyDate: this.startPolicyDate,
      }));
    },
    async fetchDetails() {
      const quoteId = localStorage.getItem("quoteId") || null;
      try {
        const response = await axios.post("/motor/api/quote/portal/detail", {
          quoteId,
        });
        this.details = response.data.data || {};
        this.filterPolicies();
      } catch (error) {
        this.$toast.error("Error getting details...");
      }
    },
    filterPolicies() {
      const available = this.disableTpoNewBusiness
        ? this.policies.filter((policy) => policy.name !== THIRD_PARTY)
        : this.policies;
      if (this.isThirdPartyOnly) {
        this.filteredPolicies = available.filter(
          (policy) => policy.name === THIRD_PARTY
        );
      } else {
        this.filteredPolicies = available;
      }
    },
    async checkDoubleInsurance() {
      try {
        const retrievedPayload =
          JSON.parse(localStorage.getItem("vehicleDetails")) || {};

        if (!retrievedPayload.vehicle) {
          this.$toast.error("Vehicle details are missing.");
          return false;
        }

        const chassisNo = retrievedPayload.vehicle.chassisNumber;
        const regNo = retrievedPayload.vehicle.regNo;
        const startDate = changeFormat(this.startPolicyDate);

        if (!startDate) {
          this.$toast.error("Please select a policy start date.");
          return false;
        }

        const payload = { chassisNo, regNo, startDate };
        const response = await axios.post(
          "/motor/api/quote/checkDoubleInsurance",
          payload
        );

        // If code is 0, no double insurance, return true to proceed
        if (response.data.code === 0) {
          this.$toast.success(
            response.data.data.message || "No double insurance detected."
          );
          return true;
        }

        // Otherwise, show double insurance modal and return false
        this.showDoubleInsurance = true;
        return false;
      } catch (error) {
        this.$toast.error("Error checking double insurance. Please try again.");
        return false;
      }
    },
    ...validations,
    calculateMinDate() {
      const today = new Date();
      return today.toISOString().split("T")[0];
    },
    required(field) {
      return (v) => !!v || `${field} is required`;
    },
    selectPolicy(policy) {
      if (this.disableTpoNewBusiness && policy?.name === THIRD_PARTY) {
        this.$toast.error(TPO_DISABLED_MESSAGE);
        return;
      }
      this.selectedPolicy = policy;
      this.$store.commit("setPolicyDetails", policy);

      // Reset benefits when changing policy
      this.courtesyCarOption = null;
      this.aaMembership = null;
      this.politicalViolence = "yes";
      this.excessProtector = "yes";
    },
    async proceedToPayment(distribution = "100") {
      if (!this.isValidPolicySelection()) {
        this.$toast.error("Please ensure all required fields are filled.");
        return;
      }

      if (this.disableTpoNewBusiness && this.selectedPolicy?.name === THIRD_PARTY) {
        this.$toast.error(TPO_DISABLED_MESSAGE);
        return;
      }

      const canProceed = await this.checkDoubleInsurance();

      if (!canProceed) {
        this.$toast.error(
          "Double Insurance detected or validation failed. Halting payment process"
        );
        return;
      }

      try {
        const { customerId, quoteId, sumInsured, startDate, benefitObject } =
          await this.getPayloadData();
        const premium = this.calculatePremium();

        const payload = this.createPayload(
          customerId,
          quoteId,
          sumInsured,
          startDate,
          benefitObject,
          premium,
          distribution
        );
        const response = await this.sendPaymentRequest(payload);

        if (response.data) {
          this.$toast.success(response.data.message);
          this.$store.commit("setPolicyDetails", {
            ...this.selectedPolicy,
            startDate: this.startPolicyDate,
            benefits: benefitObject,
          });
          this.$emit("next");
        }
      } catch (error) {
        console.log(error);
        this.$toast.error("Something went wrong. Please try again.");
      }
    },

    isValidPolicySelection() {
      if (!this.startPolicyDate || !this.selectedPolicy) {
        this.$toast.error("Please select a policy and start date.");
        return false;
      }
      return true;
    },

    async getPayloadData() {
      const formPayload = JSON.parse(localStorage.getItem("formPayload")) || {};
      const customerId = formPayload.customerId;
      const quoteId = localStorage.getItem("quoteId") || null;
      const retrievedPayload =
        JSON.parse(localStorage.getItem("vehicleDetails")) || {};
      const sumInsured = retrievedPayload.vehicle.value;
      const startDate = this.startPolicyDate;

      const benefitObject = this.createBenefitObject();

      return { customerId, quoteId, sumInsured, startDate, benefitObject };
    },

    createBenefitObject() {
      const benefitObject = this.benefits.reduce((acc, benefit) => {
        acc[benefit.key] = false;
        return acc;
      }, {});

      if (this.isBimaBambaSelected) {
        this.addBimaBambaBenefits(benefitObject);
      }

      if (this.isComprehensiveSelected) {
        this.addComprehensiveBenefits(benefitObject);
      }

      return benefitObject;
    },

    addBimaBambaBenefits(benefitObject) {
      if (this.courtesyCarOption === "10") benefitObject.courtesyCar10 = true;
      else if (this.courtesyCarOption === "20")
        benefitObject.courtesyCar20 = true;

      if (this.aaMembership === "yes") benefitObject.aa = true;
    },

    addComprehensiveBenefits(benefitObject) {
      if (this.courtesyCarOption === "10") benefitObject.courtesyCar10 = true;
      else if (this.courtesyCarOption === "20")
        benefitObject.courtesyCar20 = true;

      if (this.aaMembership === "yes") benefitObject.aa = true;
      if (this.politicalViolence === "yes")
        benefitObject.politicalViolence = true;
      if (this.excessProtector === "yes") benefitObject.excessProtector = true;
    },

    calculatePremium() {
      let premium = this.selectedPolicy.price
        .replace("Kshs. ", "")
        .replace(",", "");
      if (this.isBimaBambaSelected) {
        premium = this.bimaBambaPremium.toString();
      }
      return premium;
    },

    createPayload(
      customerId,
      quoteId,
      sumInsured,
      startDate,
      benefitObject,
      premium,
      distribution = "100"
    ) {
      const payload = {
        agentId: "",
        benefit: benefitObject,
        origin: "mobile",
        coverType: this.selectedPolicy.name,
        customerId,
        distribution,
        freeBenefits: this.getFreeBenefits(),
        premium,
        quoteId,
        startDate,
        sumInsured,
        subsequentialInstalment: 1,
      };

      return payload;
    },

    getFreeBenefits() {
      const retrievedPayload =
        JSON.parse(localStorage.getItem("vehicleDetails")) || {};
      return [
        {
          benefitValue: retrievedPayload.vehicle.entertainmentValue,
          description: "ENTERTAINMENT",
        },
        {
          benefitValue: retrievedPayload.vehicle.windscreenValue,
          description: "WINDSCREEN",
        },
      ];
    },

    async sendPaymentRequest(payload) {
      return await axios.post("/motor/api/quote/benefit", payload);
    },
    handlePaymentPlan(distribution) {
      this.showSelectPaymentPlan = false;
      this.proceedToPayment(distribution);
    },
  },
};
</script>

<style scoped>
.policy-start-date {
  height: 60px;
  font-size: 1.2rem;
}
.policy-type {
  padding: 2rem;
}
.tw-bg-blue-600 {
  background-color: #0833b3 !important;
}
.tw-text-blue-600 {
  color: #0833b3 !important;
}
.tw-font-semibold {
  font-weight: 600 !important;
}
.chargeable-benefits {
  border-radius: 8px;
  padding: 16px;
}
.benefit-option {
  background-color: #e6f0ff;
  padding: 16px;
  border-radius: 8px;
}
@media (max-width: 768px) {
  .benefit-option {
    flex-direction: column;
    align-items: flex-start;
  }
  .benefit-option > .tw-flex-grow {
    margin-top: 8px;
  }
  .chargeable-benefits {
    padding: 12px;
  }
}
</style>
